import { useEffect, useMemo, useRef } from 'react';
import { Virtuoso } from "react-virtuoso";

import useMessages from '@/hooks/useMessages';
import useStateHandler from '@/hooks/useStateHandler';
import ChatMessage from '@/shared-components/cards/components/ChatMessage';
import ChatTimestamp from '@/shared-components/cards/components/ChatTimestamp';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import Loader from '@/shared-components/loader/Loader'; 
import useGalleryStickers from '@/shared-components/stickers/useGalleryStickers';
import Utils from '@/utils';

import ConversationInput from './components/ConversationInput';
import EmptyConversation from './components/EmptyConversation';

import './index.css';

export default function Conversation({ close, orderData }) {
  const { loading, messages, loadMoreMessages, sendMessage, sendMessageTrigger, markMessagesAsRead } = useMessages(orderData._id);
  const { state, cacheHandler } = useStateHandler();
  const currentUserId = state?.user_session?._id ?? null;
  const orderBuyerId = orderData?.order_buyer?._id ?? orderData?.order_buyer ?? null;
  const isBuyer = Boolean(
    currentUserId &&
    orderBuyerId &&
    String(currentUserId) === String(orderBuyerId)
  );
  const chatRef = useRef();
  const galleryCandidates = useMemo(
    () => [
      orderData?.order_gallery,
      orderData?.order_gallery?.gallery,
      orderData?.order_artwork?.artwork_about?.artwork_gallery,
      orderData?.order_artwork?.artwork_gallery,
      orderData?.order_artwork?.artwork_about?.artwork_gallery_artist?.artist_gallery,
      orderData?.order_seller?.user_gallery,
      orderData?.order_gallery_id,
      orderData?.order_artwork?.artwork_about?.artwork_gallery_id,
      orderData?.order_artwork?.artwork_gallery_id,
    ],
    [orderData],
  );
  const { galleryId, stickers, stickersLoading } = useGalleryStickers(galleryCandidates);
  const normalizedMessages = messages ?? { data: [], totalDocs: 0 };
  const messageList = normalizedMessages.data;
  const totalDocs = normalizedMessages.totalDocs;

  useEffect(() => {
    if (!chatRef.current || !totalDocs) return;

    // Without the setTimeout it does not work haha.
    setTimeout(() => {
      chatRef.current.scrollToIndex(totalDocs);
    }, 5);
  }, [sendMessageTrigger, totalDocs]);

  useEffect(() => {
    if (loading) return;

    let active = true;
    markMessagesAsRead()
      .then((markedCount) => {
        if (active && markedCount > 0) {
          cacheHandler.triggerAction("ORDER_MESSAGES_READ");
        }
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [cacheHandler, loading, markMessagesAsRead]);

  function shouldRenderTimestamp(index) {
    const positionInFakeIndex = (totalDocs - 1) - index;
    if (positionInFakeIndex < 0) return false;
    const realIndex = (messageList.length - 1) - positionInFakeIndex;

    if (index === 0) {
      return true;
    } else if (messageList[realIndex - 1]) {
      const dayOfCurrentMessage = new Date(messageList[realIndex].createdAt).getTime();
      const dayOfPreviousMessage = new Date(messageList[realIndex - 1].createdAt).getTime();
      return !Utils.isSameDay(dayOfCurrentMessage, dayOfPreviousMessage);
    } else {
      return false;
    }
  }

  return (
    <div className="order-conversation__container">
      <header className="order-conversation__header element-non-selectable" onClick={close}>
        <BackArrowIcon className="order-conversation__header-icon" />
        <span className="order-conversation__header-text dots-on-overflow">
          Chat with{" "}
          {isBuyer
            ? orderData?.order_seller?.user_name ?? "Gallery"
            : orderData?.order_buyer?.user_username ?? orderBuyerId ?? "Collector"}
        </span>
      </header>

      <div className="order-conversation__main">
        {loading ? (
          <Loader />
        ) : messageList.length === 0 ? (
          <EmptyConversation orderData={orderData} />
        ) : (
          <Virtuoso
            className="order-conversation__virtuoso remove-scrollbar"
            data={messageList}
            firstItemIndex={Math.max(0, totalDocs - messageList.length)}
            components={{
              Header:
                messageList.length < totalDocs
                  ? () => (
                    <div className="order-conversation__more-messages">
                      <Loader className="order-conversation__more-messages-spinner" thin />
                    </div>
                  )
                  : null
            }}
            startReached={messageList.length < totalDocs ? loadMoreMessages : null}
            increaseViewportBy={200}
            initialTopMostItemIndex={totalDocs > 0 ? totalDocs - 1 : 0}
            ref={chatRef}
            itemContent={(index, message) => {
              // Determine whether to show the avatar:
              // If there is a next message and it's from the same sender, then do not show avatar.
              let showAvatar = true;
              if (index < messageList.length - 1) {
                const nextMessage = messageList[index + 1];
                if (nextMessage.message_sender._id === message.message_sender._id) {
                  showAvatar = false;
                }
              }

              <ConversationInput
                sendMessage={sendMessage}
                stickers={stickers}
                stickersLoading={stickersLoading}
                galleryId={galleryId}
              />;
              return (
                <div className="order-conversation__message">
                  {shouldRenderTimestamp(index) && (
                    <div className="order-conversation__timestamp">
                      <ChatTimestamp timestamp={new Date(message.createdAt).getTime()} />
                    </div>
                  )}
                  <ChatMessage messageData={message} showAvatar={showAvatar} />
                </div>
              );
            }}
          />
        )}
      </div>

      <ConversationInput sendMessage={sendMessage} />
    </div>
  );
}
